// src/pages/battle/ListBattlePage.jsx
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";
import { useEffect, useRef } from "react";

import BattlePageHeader from "../../components/battle/BattlePageHeader";
import BattleFilterBar from "../../components/battle/BattleFilterBar";
import BattleCardList from "../../components/battle/BattleCardList";
import BattleCreateFab from "../../components/battle/BattleCreateFab";
import { useBattleList } from "../../hooks/useBattleList";
import { useJWT } from "../../contexts/JWTContext";

export default function ListBattlePage() {
  const navigate = useNavigate();
  const { userId } = useJWT();

  const {
    battles,
    loading,
    hasMore,
    filters,
    setFilters,
    setFiltersReset,
    loadMore,
  } = useBattleList({
    searchKeyword: "",
    searchStartDate: "",
    modeFilter: "",
    statusFilter: "",
    genderType: "",
    userId,
    userLat: null,
    userLng: null,
    offset: 0,
    limit: 5,
  });

  const handleSelectBattle = (battleId) => {
    navigate(`/battle/getBattle?battleId=${battleId}`);
  };

  const navBtnBase =
    "flex-1 h-11 px-3 rounded-xl bg-white border border-slate-200 " +
    "shadow-sm text-sm font-semibold text-slate-700 " +
    "flex items-center justify-center gap-2";

  // ✅ 무한스크롤 sentinel
  const sentinelRef = useRef(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  // ✅ 위치 가져오기 (기존 유지)
  useEffect(() => {
    if (filters.userLat != null && filters.userLng != null) return;
    if (!("geolocation" in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords?.latitude ?? null;
        const lng = pos.coords?.longitude ?? null;
        if (lat == null || lng == null) return;

        // 위치는 필터 reset 안 해도 되니까 setFilters만
        setFilters((prev) => ({
          ...prev,
          userLat: lat,
          userLng: lng,
        }));
      },
      () => {},
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60_000 }
    );
  }, [filters.userLat, filters.userLng, setFilters]);

  return (
    <div className="h-screen flex justify-center bg-slate-100 overflow-hidden">
      {/* 모바일 컬럼 */}
      <div className="w-full max-w-md px-4 pb-20 flex flex-col h-full overflow-hidden">
        {/* ✅ 헤더/필터 고정 영역 */}
        <section
          className="
            -mx-4 shrink-0
            rounded-1xl bg-white
            border border-slate-100
            shadow-[0_6px_20px_rgba(0,0,0,0.08)]
            p-0
          "
        >
          <BattlePageHeader title="대결 리스트" />
          <div className="mt-2 flex gap-2 px-4">
            <button
              type="button"
              className={navBtnBase}
              onClick={() => navigate("/battle/top")}
            >
              <Trophy className="w-5 h-5" color="#F59E0B" strokeWidth={2.2} />
              <span>대결의 전당</span>
            </button>

            <button
              type="button"
              className={navBtnBase}
              onClick={() => navigate("/battle/my")}
            >
              <Trophy className="w-5 h-5" color="#2563EB" strokeWidth={2.2} />
              <span>내 대결 목록</span>
            </button>
          </div>

          <div className="mt-3">
            {/* ✅ 필터 변경은 항상 offset=0으로 초기화 */}
            <BattleFilterBar
              filters={filters}
              onChange={(next) => {
                // next는 전체 filters 객체로 넘어오니까 그대로 reset setter로 넣자
                setFiltersReset(next);
              }}
            />
          </div>
        </section>

        {/* ✅ 리스트만 스크롤 */}
     <main className="flex-1 mt-1 overflow-y-auto overscroll-contain bg-slate-100 pb-28">

          <BattleCardList
            battles={battles}
            loading={loading && battles.length === 0}
            onSelectBattle={handleSelectBattle}
          />

          <div ref={sentinelRef} className="h-10" />

          {loading && battles.length > 0 ? (
            <div className="py-3 text-center text-xs text-slate-400">
              더 불러오는 중...
            </div>
          ) : null}

          {!hasMore && battles.length > 0 ? (
            <div className="py-3 text-center text-xs text-slate-400">
              마지막 대결입니다.
            </div>
          ) : null}
        </main>

        <BattleCreateFab />
      </div>
    </div>
  );
}
